/**
 * The visitor's currency for the web prices. The subscription page asks RevenueCat, which picks
 * the currency of the customer's country among the web products' prices (44 currencies,
 * ESTAT-DEL-PROJECTE.md §6) or its default one, dollars. The landing does not load RevenueCat's
 * SDK (too heavy for it): it shows the annual prices of the Educators plan and the school packs from
 * the tables below, in the currency of the visitor's country guessed from the browser's time zone — the euro area's zones
 * give EUR, the zones of the other countries with a price give theirs, anywhere else USD, as
 * RevenueCat would. The tables copy the products' prices in RevenueCat: change both together.
 *
 * `?currency=PLN` (kept for the tab) asks for one currency everywhere, to check the prices.
 */

/** The landing's Teams and Schools buttons ask the Subscription page to show the school packs (sessionStorage, once). */
export const SHOW_PACKS_KEY = "makeit.showPacks";

/** The currency asked for with `?currency=`, or null. */
export const CURRENCY_OVERRIDE: string | null = (() => {
  try {
    const asked = new URLSearchParams(location.search).get("currency");
    if (asked && /^[A-Za-z]{3}$/.test(asked)) sessionStorage.setItem("makeit.currency", asked.toUpperCase());
    return sessionStorage.getItem("makeit.currency");
  } catch {
    return null;
  }
})();

/** The annual price of makeit_web_educators_annual in each currency (RevenueCat, 28/10/2026). */
export const ANNUAL_PRICES: Record<string, number> = {
  EUR: 49.99, USD: 49.99, GBP: 44.99, JPY: 8000, AUD: 79.99, CAD: 69.99, NZD: 84.99, SGD: 74.99, HKD: 398, CHF: 49.9,
  SEK: 549, NOK: 579, DKK: 375, CZK: 1249, KRW: 79000, TWD: 1690, AED: 199.99, SAR: 199.99, QAR: 199.99, ILS: 189.9,
  PLN: 149.99, HUF: 14990, RON: 174.99, CNY: 199, BRL: 149.9, MXN: 499, CLP: 24990, COP: 99900, PEN: 99.9, CRC: 17490,
  MYR: 124.9, THB: 949, PHP: 1349, IDR: 349000, VND: 499000, ZAR: 499.99, MAD: 249, KES: 2499, TZS: 44900, IQD: 29900,
  JOD: 24.99, GEL: 69.99, KZT: 13490, RSD: 3249,
};

/** The annual price of the school packs, makeit_web_teams_annual and makeit_web_schools_annual, in each currency (RevenueCat, 28/10/2026). */
export const PACK_PRICES: Record<string, [teams: number, schools: number]> = {
  EUR: [149, 299], USD: [149, 299], GBP: [129, 269], JPY: [23900, 47900], AUD: [239, 479], CAD: [209, 419], NZD: [249, 509],
  SGD: [219, 449], HKD: [1190, 2390], CHF: [149, 299], SEK: [1590, 3290], NOK: [1690, 3490], DKK: [1090, 2190],
  CZK: [3690, 7490], KRW: [239000, 469000], TWD: [4990, 9990], AED: [599, 1190], SAR: [599, 1190], QAR: [599, 1190],
  ILS: [569, 1090], PLN: [449, 899], HUF: [44900, 89900], RON: [519, 1049], CNY: [589, 1190], BRL: [449, 899],
  MXN: [1490, 2990], CLP: [73900, 149000], COP: [299000, 599000], PEN: [299, 599], CRC: [51900, 104900], MYR: [369, 749],
  THB: [2790, 5690], PHP: [3990, 7990], IDR: [1039000, 2089000], VND: [1489000, 2979000], ZAR: [1490, 2990], MAD: [739, 1490],
  KES: [7390, 14900], TZS: [129000, 269000], IQD: [88900, 179000], JOD: [73.99, 149], GEL: [209, 419], KZT: [39900, 79900],
  RSD: [9690, 19490],
};

/** The time zones of the countries with a price in their own currency (all of Australia by prefix). */
const ZONES: Record<string, string[]> = {
  EUR: [
    "Europe/Vienna", "Europe/Brussels", "Europe/Zagreb", "Asia/Nicosia", "Asia/Famagusta", "Europe/Nicosia", "Europe/Tallinn",
    "Europe/Helsinki", "Europe/Mariehamn", "Europe/Paris", "Europe/Berlin", "Europe/Busingen", "Europe/Athens", "Europe/Dublin",
    "Europe/Rome", "Europe/Riga", "Europe/Vilnius", "Europe/Luxembourg", "Europe/Malta", "Europe/Amsterdam", "Europe/Lisbon",
    "Atlantic/Madeira", "Atlantic/Azores", "Europe/Bratislava", "Europe/Ljubljana", "Europe/Madrid", "Africa/Ceuta",
    "Atlantic/Canary", "Europe/Sofia", "Europe/Andorra", "Europe/Monaco", "Europe/San_Marino", "Europe/Vatican",
    "Europe/Podgorica", "Indian/Reunion", "Indian/Mayotte", "America/Martinique", "America/Guadeloupe", "America/Cayenne",
    "America/St_Barthelemy", "America/Marigot", "America/Miquelon",
  ],
  GBP: ["Europe/London", "Europe/Belfast", "Europe/Isle_of_Man", "Europe/Jersey", "Europe/Guernsey"],
  CHF: ["Europe/Zurich", "Europe/Vaduz"],
  SEK: ["Europe/Stockholm"],
  NOK: ["Europe/Oslo", "Arctic/Longyearbyen"],
  DKK: ["Europe/Copenhagen", "Atlantic/Faroe", "America/Nuuk", "America/Godthab", "America/Danmarkshavn", "America/Scoresbysund", "America/Thule"],
  CZK: ["Europe/Prague"],
  PLN: ["Europe/Warsaw"],
  HUF: ["Europe/Budapest"],
  RON: ["Europe/Bucharest"],
  RSD: ["Europe/Belgrade"],
  GEL: ["Asia/Tbilisi"],
  KZT: ["Asia/Almaty", "Asia/Qostanay", "Asia/Aqtobe", "Asia/Aqtau", "Asia/Atyrau", "Asia/Oral", "Asia/Qyzylorda"],
  JPY: ["Asia/Tokyo"],
  KRW: ["Asia/Seoul"],
  CNY: ["Asia/Shanghai", "Asia/Urumqi", "Asia/Chongqing", "Asia/Harbin"],
  TWD: ["Asia/Taipei"],
  HKD: ["Asia/Hong_Kong"],
  SGD: ["Asia/Singapore"],
  MYR: ["Asia/Kuala_Lumpur", "Asia/Kuching"],
  THB: ["Asia/Bangkok"],
  PHP: ["Asia/Manila"],
  IDR: ["Asia/Jakarta", "Asia/Pontianak", "Asia/Makassar", "Asia/Jayapura"],
  VND: ["Asia/Ho_Chi_Minh", "Asia/Saigon"],
  AED: ["Asia/Dubai"],
  SAR: ["Asia/Riyadh"],
  QAR: ["Asia/Qatar"],
  ILS: ["Asia/Jerusalem", "Asia/Tel_Aviv"],
  JOD: ["Asia/Amman"],
  IQD: ["Asia/Baghdad"],
  NZD: ["Pacific/Auckland", "Pacific/Chatham"],
  CAD: [
    "America/Toronto", "America/Montreal", "America/Vancouver", "America/Edmonton", "America/Winnipeg", "America/Halifax",
    "America/St_Johns", "America/Regina", "America/Swift_Current", "America/Moncton", "America/Glace_Bay", "America/Goose_Bay",
    "America/Whitehorse", "America/Dawson", "America/Dawson_Creek", "America/Fort_Nelson", "America/Creston", "America/Iqaluit",
    "America/Rankin_Inlet", "America/Resolute", "America/Cambridge_Bay", "America/Inuvik", "America/Atikokan",
    "America/Blanc-Sablon", "America/Thunder_Bay", "America/Nipigon", "America/Rainy_River", "America/Pangnirtung", "America/Yellowknife",
  ],
  MXN: [
    "America/Mexico_City", "America/Cancun", "America/Merida", "America/Monterrey", "America/Matamoros", "America/Chihuahua",
    "America/Ciudad_Juarez", "America/Ojinaga", "America/Mazatlan", "America/Bahia_Banderas", "America/Hermosillo", "America/Tijuana",
    "America/Ensenada",
  ],
  BRL: [
    "America/Sao_Paulo", "America/Rio_Branco", "America/Manaus", "America/Belem", "America/Fortaleza", "America/Recife",
    "America/Bahia", "America/Maceio", "America/Araguaina", "America/Cuiaba", "America/Campo_Grande", "America/Porto_Velho",
    "America/Boa_Vista", "America/Santarem", "America/Noronha", "America/Eirunepe",
  ],
  CLP: ["America/Santiago", "America/Punta_Arenas", "Pacific/Easter"],
  COP: ["America/Bogota"],
  PEN: ["America/Lima"],
  CRC: ["America/Costa_Rica"],
  ZAR: ["Africa/Johannesburg"],
  MAD: ["Africa/Casablanca", "Africa/El_Aaiun"],
  KES: ["Africa/Nairobi"],
  TZS: ["Africa/Dar_es_Salaam"],
};

const BY_ZONE = new Map(Object.entries(ZONES).flatMap(([currency, zones]) => zones.map((z) => [z, currency] as const)));

/** The currency of the country of an IANA time zone: its own when it has a price, else USD. */
export function currencyOfTimeZone(zone: string): string {
  if (zone.startsWith("Australia/")) return "AUD";
  return BY_ZONE.get(zone) ?? "USD";
}

/** The visitor's currency: the one asked for, else that of the browser's time zone. */
export function visitorCurrency(): string {
  if (CURRENCY_OVERRIDE && CURRENCY_OVERRIDE in ANNUAL_PRICES) return CURRENCY_OVERRIDE;
  try {
    return currencyOfTimeZone(Intl.DateTimeFormat().resolvedOptions().timeZone ?? "");
  } catch {
    return "EUR";
  }
}

/** The Educators plan's annual price in the visitor's currency. */
export function annualPrice(): { amount: number; currency: string } {
  const currency = visitorCurrency();
  return { amount: ANNUAL_PRICES[currency] ?? ANNUAL_PRICES.EUR, currency: ANNUAL_PRICES[currency] ? currency : "EUR" };
}

/** The annual price of a school pack (Teams, Schools) in the visitor's currency. */
export function packPrice(pack: "teams" | "schools"): { amount: number; currency: string } {
  const { currency } = annualPrice();
  return { amount: (PACK_PRICES[currency] ?? PACK_PRICES.EUR)[pack === "teams" ? 0 : 1], currency };
}
